'use client';

import { useCallback } from 'react';
import { popup, type ShowPopupOptions } from '../bootstrap/tmaActions';

export type { ShowPopupOptions, ShowPopupOptionsButton } from '../bootstrap/tmaActions';

export function useConfirmPopup() {
  return useCallback(async (options: ShowPopupOptions): Promise<string | null> => {
    try {
      const result = await popup.open(options);
      // popup.open returns null if SDK not available (plain web) or user dismissed.
      // Distinguish "SDK unavailable" from "user dismissed" only via fallback when needed.
      // If we got a non-null answer, it's the SDK's reply.
      if (result !== null) return result;
      // result === null could mean either dismissal in TMA or no SDK on plain web.
      // Fall through to native fallback only when there's no usable Telegram SDK.
      if (typeof window === 'undefined') return null;
      const hasTma = window.location.hash.includes('tgWebAppData=');
      if (hasTma) return null; // genuine dismissal in TMA
    } catch (err) {
      console.log('Telegram popup not available, using fallback');
    }

    const buttons = options.buttons;
    if (!buttons || buttons.length <= 1) {
      window.alert(options.title ? `${options.title}\n\n${options.message}` : options.message);
      return buttons?.[0] && 'id' in buttons[0] ? (buttons[0].id ?? null) : null;
    }

    const isConfirmed = window.confirm(
      options.title ? `${options.title}\n\n${options.message}` : options.message,
    );
    const confirmBtn = buttons.find((b) => 'id' in b && b.id === 'confirm');
    const cancelBtn = buttons.find(
      (b) => 'id' in b && (b.id === 'cancel' || ('type' in b && b.type === 'cancel')),
    );

    if (isConfirmed) {
      return confirmBtn && 'id' in confirmBtn ? (confirmBtn.id ?? 'confirm') : 'confirm';
    }
    return cancelBtn && 'id' in cancelBtn ? (cancelBtn.id ?? 'cancel') : 'cancel';
  }, []);
}
